import * as z from 'zod'

import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import type { StepConfigParams } from '@flows/configs/types'
import type { AnyStepConfiguration } from '@flows/core/types/stepTypes'
import { BaseSchema_DentalV1GetsafeDe } from '@flows/insurance/schemas'

import {
  eligibilitySchemaShape,
  generalNotice,
  getIneligibleStep,
  healthInsuranceTypeChoice,
  infoTooltip,
  missingTeethChoice,
  periodontalDiseaseChoice,
} from './questions'

const getErrorMap = (): z.ZodErrorMap => (issue, ctx) => {
  return (
    schemaUtils.bornOnErrorMap(issue, {
      min: 'sharedFlows_dental:steps.dental_instant.errorTooYoung',
      max: 'sharedFlows_dental:steps.dental_instant.errorTooOld',
    }) ?? { message: ctx.defaultError }
  )
}

const dental_instant = (params: StepConfigParams) => {
  const getParamPath = params.helperFunctions.getParamPath

  return {
    schema: z.object({
      born_on: BaseSchema_DentalV1GetsafeDe.shape.born_on,
      ...schemaUtils.generateZodObjectFromPaths(eligibilitySchemaShape(getParamPath)).shape,
    }),
    errorMap: getErrorMap(),
    buttonLabel: {
      type: 'translatableText',
      translationKey: 'sharedFlows_dental:steps.dental_instant.submitButton',
    },
    stepContent: [
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_dental:steps.dental_instant.birthday.title',
        },
        infoButton: infoTooltip('sharedFlows_dental:steps.dental_instant.birthday.info.text'),
      },
      {
        type: 'date',
        shouldTrackValues: true,
        name: 'born_on',
        disabledIfValueExists: true,
      },
      {
        type: 'spacer',
        size: 'medium',
      },
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_dental:steps.dental_instant.health_insurance.title',
        },
        infoButton: infoTooltip(
          'sharedFlows_dental:steps.dental_instant.health_insurance.info.text'
        ),
      },
      healthInsuranceTypeChoice(getParamPath),
      {
        type: 'spacer',
        size: 'medium',
      },
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_dental:steps.dental_instant.missing_teeth.title',
        },
        infoButton: infoTooltip('sharedFlows_dental:steps.dental_instant.missing_teeth.info.text'),
      },
      missingTeethChoice(getParamPath),
      {
        type: 'spacer',
        size: 'medium',
      },
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_dental:steps.dental_instant.periodontal_disease.title',
        },
        infoButton: infoTooltip(
          'sharedFlows_dental:steps.dental_instant.periodontal_disease.info.text'
        ),
      },
      periodontalDiseaseChoice(getParamPath),
      {
        type: 'spacer',
        size: 'large',
      },
      generalNotice,
    ],
    subFlow: params.subFlow,
    nextStepConfig: {
      valueFunction: (flowValues) => getIneligibleStep(flowValues, getParamPath) ?? 'dental_quote',
      valueToStepName: {
        dental_quote: 'dental_quote',
        failure: 'failure',
        dental_already_covered: 'dental_already_covered',
      },
    },
  } as const satisfies AnyStepConfiguration
}

export default dental_instant
